<template>
    <app-layout title="Edit Order">
        <form @submit.prevent="handleSubmit">
            <page-header>
                <template #title>Edit Order</template>

                <template #actions>
                    <jet-button v-show="form.isDirty" type="submit" class="leading-none" :disabled="form.processing" >
                        Save
                    </jet-button>

                    <jet-tertiary-button
                        v-if="form.isDirty"
                        :disabled="form.processing"
                        class="leading-none"
                        @click="showUnsavedChangesModal = true"
                    >
                        Cancel
                    </jet-tertiary-button>

                    <jet-tertiary-button
                        v-else
                        :disabled="form.processing"
                        :href="backUrl"
                        class="leading-none"
                    >
                        Close
                    </jet-tertiary-button>
                </template>
            </page-header>

            <customer-form
                v-if="order.is_wholesale"
                :customers="customers"
                :holidays="holidays"
                :errors="errors"
                :form="form"
                :can_edit_picking_notes="can_edit_picking_notes"
                class="px-3 md:px-4 lg:px-8"
            />
            <div v-else class="flex flex-col px-3 md:px-4 lg:px-8 gap-y-4 ">
                <order-details
                    :order="order"
                    :show-placed-by-stamp="true"
                    :key="order.status"
                />
            </div>

            <div v-if="order.status != 2 && order.payment_status == 'paid'" class="px-3 md:px-4 lg:px-8 py-4 text-red-600 font-bold">
                This order is already paid for. Please manually refund for any order changes as necessary
            </div>

            <div class="px-3 md:px-4 lg:px-8 py-4">
                <order-items v-if="order.status === 2" :order="order" />
                <order-items-edit
                    v-else
                    :order="order"
                    :form="form"
                    :customers="customers"
                    :is_wholesale="order.is_wholesale"
                    :orderMinimum="orderMinimum"
                    :deliveryFeeBelowMinimum="deliveryFeeBelowMinimum"
                    :manualPickingMode="manualPickingMode"
                    @updateCanPlaceOrder="updateCanPlaceOrder"
                />
            </div>

            <div class="text-center py-24" >
                <p v-if="order.status === -1" class="text-2xl text-red-700 opacity-50">--- Cancelled ---</p>

                <div v-else class="flex justify-center gap-x-8">
                    <jet-danger-button v-if="order.status === 0 || order.status === 1" @click="openCancelModal">
                        Cancel order
                    </jet-danger-button>
                </div>

            </div>
        </form>

        <unsaved-changes-modal-2
            :formRoute="formRoute"
            :isDirty="form.isDirty"
            :show="showUnsavedChangesModal"
            @close="showUnsavedChangesModal = false"
            @confirm="$inertia.get(backUrl)"
        />

        <jet-dialog-modal :show="showCancelModal" @close="closeCancelModal">
            <template #title>
                Cancel Order
            </template>

            <template #content>
                <p class="mb-3">Are you sure you would like to cancel this order?</p>
                <jet-label for="cancellation_reason" value="Cancellation reason" class="mb-1" />
                <textarea
                    id="cancellation_reason"
                    v-model="cancellationReason"
                    class="w-full rounded border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                    rows="5"
                    @input="cancellationReasonError = null"
                />
                <jet-input-error class="mt-2" :message="cancellationReasonError || $page.props.errors.cancellation_reason" />
            </template>

            <template #footer>
                <jet-danger-button
                    :class="{ 'opacity-25': cancelOrderProcessing || form.processing }"
                    :disabled="cancelOrderProcessing || form.processing"
                    @click="submitCancelOrder"
                >
                    Cancel order
                </jet-danger-button>

                <jet-tertiary-button class="ml-2" :disabled="cancelOrderProcessing || form.processing" @click="closeCancelModal">
                    Close
                </jet-tertiary-button>
            </template>
        </jet-dialog-modal>

        <jet-dialog-modal :show="showInvoicedModal" @close="showInvoicedModal=false">
            <template #title>
                Mark Order Invoiced
            </template>

            <template #content>
                Are you sure you would like to mark this order invoiced?
            </template>

            <template #footer>
                <jet-button @click="markOrderInvoiced">
                    Yes
                </jet-button>

                <jet-tertiary-button class="ml-2" @click="showInvoicedModal=false">
                    No
                </jet-tertiary-button>
            </template>
        </jet-dialog-modal>

    </app-layout>
</template>

<script>
import { useForm } from '@inertiajs/inertia-vue3';
import { DateTime } from "luxon";
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import OrderDetails from '@/Shared/Orders/OrderDetails.vue';
import CustomerForm from '@/Pages/Orders/Partials/CustomerForm.vue';
import OrderItemsEdit from '@/Shared/Orders/OrderItemsEdit';
import OrderItems from '@/Shared/Orders/OrderItems';
import JetButton from '@/Jetstream/Button';
import JetDialogModal from '@/Jetstream/DialogModal';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import JetInputError from '@/Jetstream/InputError';
import JetLabel from '@/Jetstream/Label';
import UnsavedChangesModal2 from '@/Shared/UnsavedChangesModal2';
import { slimProductForOrderSubmit } from '@/Shared/Orders/slimProductForOrderSubmit';

export default {
    setup(props) {
        const form = useForm({
            id: props.order.id,
            allow_substitutions: props.order.allow_substitutions,
            order_date: new Date(props.order.order_date + ' EST'),
            ship_date: new Date(props.order.ship_date + ' EST'),
            shipping_method: props.order.shipping_method,
            payment_status: props.order.payment_status,
            override_ship_date: props.order.override_ship_date,
            customer_id: props.order.customer_id,
            delivery_fee: props.order.delivery_fee,
            applied_credit_amount: props.order.applied_credit_amount,
            notes: props.order.notes,
            picking_notes: props.order.picking_notes,
            skills: Array.isArray(props.order.skills) ? props.order.skills : [],
            vehicle_features: Array.isArray(props.order.vehicle_features) ? props.order.vehicle_features : [],
            priority: 'M',
            optimoroute_error: props.order.optimoroute_error,
            is_pricing_locked: props.order.is_pricing_locked,
            is_admin: props.is_admin,
            override_stock_restrictions: false,
            payment_terms: props.order.payment_terms,
            payment_method: props.order.payment_method,
            status: props.order.status,
            po_num: props.order.po_num,
            placed_by_name: props.order.user?.name,
            order_items: props.order.order_items.map(i => {
                return {
                    id: i.id,
                    order_id: i.order_id,
                    promo_code_id: i.promo_code_id,
                    product_id: i.product_id,
                    sfd_product: i.sfd_product,
                    substitution: i.substitution,
                    title: i.title,
                    description: i.description,
                    product_notes: i.product_notes,
                    size: i.size,
                    tax: i.tax,
                    partial_qty: i.partial_qty,
                    cost: i.cost,
                    price: i.price,
                    quantity: i.quantity,
                    orig_quantity: i.orig_quantity,
                    product: i.product,
                    zone: i.zone,
                }
            }),
        })

        return { form }
    },

    components: {
        AppLayout,
        PageHeader,
        OrderDetails,
        CustomerForm,
        OrderItemsEdit,
        OrderItems,
        JetButton,
        JetDangerButton,
        JetDialogModal,
        JetTertiaryButton,
        JetInputError,
        JetLabel,
        UnsavedChangesModal2,
    },

    props: ['order', 'customers', 'holidays', 'errors', 'orderMinimum', 'deliveryFeeBelowMinimum', 'is_admin', 'manualPickingMode', 'can_edit_picking_notes'],

    data() {
        return {
            formRoute: this.route('orders.update', this.order.id),
            canPlaceOrder: false,
            showCancelModal: false,
            showInvoicedModal: false,
            showUnsavedChangesModal: false,
            cancellationReason: '',
            cancellationReasonError: null,
            cancelOrderProcessing: false,
        }
    },

    methods: {
        openCancelModal () {
            this.cancellationReason = '';
            this.cancellationReasonError = null;
            this.showCancelModal = true;
        },

        closeCancelModal () {
            this.showCancelModal = false;
            this.cancellationReason = '';
            this.cancellationReasonError = null;
            this.cancelOrderProcessing = false;
        },

        submitCancelOrder () {
            const reason = (this.cancellationReason || '').trim();

            if (!reason) {
                this.cancellationReasonError = 'Please enter a cancellation reason.';
                return;
            }

            if (this.form.processing || this.cancelOrderProcessing) {
                return;
            }

            this.cancellationReasonError = null;
            this.cancelOrderProcessing = true;

            this.$inertia.delete(route('orders.destroy', this.order.id), {
                data: { cancellation_reason: reason },
                preserveScroll: true,
                onFinish: () => {
                    this.cancelOrderProcessing = false;
                },
                onSuccess: () => {
                    this.closeCancelModal();
                },
            });
        },

        handleSubmit () {
            if(!this.form.processing) {
                const returnUrl = route().params.return;

                this.form
                    .transform((data) => ({
                        ...data,
                        order_date: data.order_date ? this.formatDate(data.order_date) : null,
                        ship_date: data.ship_date ? this.formatDate(data.ship_date) : null,
                        manualPickingMode: this.manualPickingMode,
                        order_items: data.order_items.map(({ customer_price, customer_split_case_price, last_quantity_for_price, product, ...item }) => ({
                            ...item,
                            product: slimProductForOrderSubmit(product),
                        })),
                    }))
                    .submit('put', route('orders.update', {
                        order: this.order.id,
                        return: returnUrl,
                    }));
            }
        },

        formatDate (date) {
            // Takes a Javascript date object and returns it in formay 'YYYY-MM-DD'
            return date.toISOString().split('T')[0];
        },

        updateCanPlaceOrder(value) {
            this.canPlaceOrder = value;
        },

    },

    computed: {
        backUrl () {
            const params = route().params;

            if (params.return) {
                return params.return;
            }

            return route('orders.index', {
                page: this.$page.props.page.orders,
            });
        },
    },
}
</script>

<style>

</style>
